// Context pour gérer le chargement des composants
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { LoadingQueueService, TaskPriority } from '../Services/LoadingQueueService';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';

export interface ComponentLoadState {
    isLoading: boolean;
    data: any;
    error: Error | null;
    priority: TaskPriority;
}

export interface RegisteredComponent {
    id: string;
    ref: React.RefObject<HTMLElement>;
    priority: TaskPriority;
    loadState: ComponentLoadState;
    isVisible: boolean;
}

interface ComponentLoadingContextValue {
    // State
    components: Map<string, RegisteredComponent>;
    
    // Actions
    registerComponent: (
        id: string, 
        ref: React.RefObject<HTMLElement>, 
        priority: TaskPriority
    ) => void;
    unregisterComponent: (id: string) => void;
    loadComponentData: (id: string, loader: () => Promise<any>) => Promise<any>;
    getComponentData: (id: string) => any;
    setPriority: (id: string, priority: TaskPriority) => void;
    setVisible: (id: string, visible: boolean) => void;
}

const ComponentLoadingContext = createContext<ComponentLoadingContextValue | null>(null);

export const ComponentLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [components, setComponents] = useState<Map<string, RegisteredComponent>>(new Map());
    const componentsRef = useRef<Map<string, RegisteredComponent>>(new Map());

    // Sync ref with state
    useEffect(() => {
        componentsRef.current = components;
    }, [components]);

    /**
     * Enregistre un composant
     */
    const registerComponent = useCallback((
        id: string,
        ref: React.RefObject<HTMLElement>,
        priority: TaskPriority
    ) => {
        setComponents(prev => {
            const newMap = new Map(prev);
            newMap.set(id, {
                id,
                ref,
                priority,
                isVisible: false,
                loadState: {
                    isLoading: false,
                    data: null,
                    error: null,
                    priority
                }
            });
            return newMap;
        });

        LazyLoadLogger.logData(`Composant enregistré: ${id}`, {
            id,
            priority: getPriorityName(priority)
        });
    }, []);

    /**
     * Désenregistre un composant
     */
    const unregisterComponent = useCallback((id: string) => {
        setComponents(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });

        LazyLoadLogger.logData(`Composant désenregistré: ${id}`);
    }, []);

    /**
     * Charge les données d'un composant
     */
    const loadComponentData = useCallback(async (
        id: string,
        loader: () => Promise<any>
    ): Promise<any> => {
        const component = componentsRef.current.get(id);
        if (!component) {
            throw new Error(`Component ${id} not registered`);
        }

        // Si déjà chargé, retourner les données
        if (component.loadState.data !== null) {
            LazyLoadLogger.logData(`Données en cache pour: ${id}`);
            return component.loadState.data;
        }

        // Si déjà en cours de chargement, attendre
        if (component.loadState.isLoading) {
            LazyLoadLogger.logData(`Chargement déjà en cours pour: ${id}`);
            return new Promise((resolve, reject) => {
                const checkInterval = setInterval(() => {
                    const comp = componentsRef.current.get(id);
                    if (!comp) {
                        clearInterval(checkInterval);
                        reject(new Error('Component unregistered'));
                        return;
                    }
                    
                    if (!comp.loadState.isLoading) {
                        clearInterval(checkInterval);
                        if (comp.loadState.error) {
                            reject(comp.loadState.error);
                        } else {
                            resolve(comp.loadState.data);
                        }
                    }
                }, 100);
            });
        }

        // Marquer comme en cours de chargement
        setComponents(prev => {
            const newMap = new Map(prev);
            const comp = newMap.get(id);
            if (comp) {
                comp.loadState.isLoading = true;
                comp.loadState.error = null;
            }
            return newMap;
        });

        LazyLoadLogger.logData(`Chargement des données pour: ${id}`, {
            id,
            priority: getPriorityName(component.priority)
        });

        // Ajouter à la queue si pas en priorité P0
        if (component.priority === TaskPriority.P0_VIEWPORT) {
            // Charger immédiatement
            try {
                const data = await loader();
                
                setComponents(prev => {
                    const newMap = new Map(prev);
                    const comp = newMap.get(id);
                    if (comp) {
                        comp.loadState.isLoading = false;
                        comp.loadState.data = data;
                    }
                    return newMap;
                });

                LazyLoadLogger.logData(`✅ Données chargées pour: ${id}`);
                return data;
            } catch (error) {
                const err = error instanceof Error ? error : new Error(String(error));
                
                setComponents(prev => {
                    const newMap = new Map(prev);
                    const comp = newMap.get(id);
                    if (comp) {
                        comp.loadState.isLoading = false;
                        comp.loadState.error = err;
                    }
                    return newMap;
                });

                LazyLoadLogger.error('data', `❌ Erreur de chargement pour: ${id}`, error);
                throw err;
            }
        } else {
            // Ajouter à la queue
            return new Promise((resolve, reject) => {
                LoadingQueueService.enqueue({
                    id: `component-data-${id}`,
                    priority: component.priority,
                    execute: async () => {
                        try {
                            const data = await loader();
                            
                            setComponents(prev => {
                                const newMap = new Map(prev);
                                const comp = newMap.get(id);
                                if (comp) {
                                    comp.loadState.isLoading = false;
                                    comp.loadState.data = data;
                                }
                                return newMap;
                            });

                            LazyLoadLogger.logData(`✅ Données chargées pour: ${id}`);
                            resolve(data);
                            return data;
                        } catch (error) {
                            const err = error instanceof Error ? error : new Error(String(error));
                            
                            setComponents(prev => {
                                const newMap = new Map(prev);
                                const comp = newMap.get(id);
                                if (comp) {
                                    comp.loadState.isLoading = false;
                                    comp.loadState.error = err;
                                }
                                return newMap;
                            });

                            LazyLoadLogger.error('data', `❌ Erreur de chargement pour: ${id}`, error);
                            reject(err);
                            throw err;
                        }
                    }
                });
            });
        }
    }, []);

    /**
     * Récupère les données d'un composant
     */
    const getComponentData = useCallback((id: string): any => {
        const component = components.get(id);
        return component?.loadState.data || null;
    }, [components]);

    /**
     * Change la priorité d'un composant
     */
    const setPriority = useCallback((id: string, priority: TaskPriority) => {
        setComponents(prev => {
            const newMap = new Map(prev);
            const comp = newMap.get(id);
            if (comp) {
                comp.priority = priority;
                comp.loadState.priority = priority;
                
                LazyLoadLogger.logData(`Priorité changée pour ${id}: ${getPriorityName(priority)}`);
            }
            return newMap;
        });
    }, []);

    /**
     * Marque un composant comme visible/invisible
     */
    const setVisible = useCallback((id: string, visible: boolean) => {
        setComponents(prev => {
            const newMap = new Map(prev);
            const comp = newMap.get(id);
            if (comp && comp.isVisible !== visible) {
                comp.isVisible = visible;
                
                LazyLoadLogger.logViewport(
                    visible 
                        ? `Composant entré dans le viewport: ${id}` 
                        : `Composant sorti du viewport: ${id}`
                );

                // Si le composant devient visible, augmenter sa priorité
                if (visible && comp.priority > TaskPriority.P0_VIEWPORT) {
                    comp.priority = TaskPriority.P0_VIEWPORT;
                    comp.loadState.priority = TaskPriority.P0_VIEWPORT;
                }
            }
            return newMap;
        });
    }, []);

    const value: ComponentLoadingContextValue = {
        components,
        registerComponent,
        unregisterComponent,
        loadComponentData,
        getComponentData,
        setPriority,
        setVisible
    };

    return (
        <ComponentLoadingContext.Provider value={value}>
            {children}
        </ComponentLoadingContext.Provider>
    );
};

/**
 * Hook pour utiliser le context
 */
export function useComponentLoading() {
    const context = useContext(ComponentLoadingContext);
    if (!context) {
        throw new Error('useComponentLoading must be used within ComponentLoadingProvider');
    }
    return context;
}

/**
 * Helper pour obtenir le nom d'une priorité
 */
function getPriorityName(priority: TaskPriority): string {
    switch (priority) {
        case TaskPriority.P0_VIEWPORT:
            return 'P0: Viewport';
        case TaskPriority.P1_PRELOAD:
            return 'P1: Préchargement';
        case TaskPriority.P2_PAGES:
            return 'P2: Pages';
        case TaskPriority.P3_MEDIA_UPGRADE:
            return 'P3: Médias HQ';
        default:
            return `P${priority}`;
    }
}



